import { useEffect, useMemo, useState } from 'react'
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded'
import BrushRoundedIcon from '@mui/icons-material/BrushRounded'
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded'
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import { useAuth } from '../../context/useAuth'
import { useToast } from '../../context/useToast'
import { useWorkspace } from '../../context/useWorkspace'
import { useDesignAssets } from '../../hooks/useDesignAssets'
import { useFormDefinitions } from '../../hooks/useFormDefinitions'
import {
  attachDesignToItem,
  attachFormToItem,
  attachLinkToItem,
  attachUploadToItem,
  detachDesignFromItem,
  detachFormFromItem,
  detachLinkFromItem,
  detachUploadFromItem,
  type ItemAttachmentTarget,
} from '../../services/itemAttachmentService'
import { subscribeWorkspaceFiles } from '../../services/storageHubService'
import type { WorkspaceFileMetadata } from '../../types/schema'
import { Button, MiniLoader, TagChip } from '../Common'
import { AppDialog } from '../Common/AppDialog'
import { WorkspaceItemPicker } from '../Common/WorkspaceItemPicker'
import type { WorkspaceItemReference } from '../Common/WorkspaceItemPicker'
import './forms.css'

const EMPTY_IDS: string[] = []

const formatMinorCurrency = (value: unknown, currency = 'USD') => {
  if (typeof value !== 'number') return 'Not allotted'
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value / 100)
}

export type ProvisionKind = 'forms' | 'designs' | 'uploads' | 'links'

export const ItemAttachmentsPanel = ({
  target,
  budgetLimit,
  estimatedCost,
  canEdit,
  onSetBudgetLimit,
  attachTourId,
}: {
  target: ItemAttachmentTarget
  budgetLimit?: number | null
  estimatedCost?: number | null
  canEdit: boolean
  onSetBudgetLimit?: () => void
  attachTourId?: string
}) => {
  const { currentUser } = useAuth()
  const { activeWorkspaceId, workspaces } = useWorkspace()
  const { showToast } = useToast()
  const formsPage = useFormDefinitions({
    workspaceId: target.workspaceId || activeWorkspaceId,
    lifecycleStatus: 'ACTIVE',
    search: '',
    sort: 'NEWEST',
    cursor: null,
  })
  const designsPage = useDesignAssets(target.workspaceId || activeWorkspaceId || null, { status: 'COMPLETED' })
  const [workspaceFiles, setWorkspaceFiles] = useState<WorkspaceFileMetadata[]>([])
  const [workspaceLinks] = useState<Array<{ id: string; title: string; url?: string }>>([])

  const [optimisticFormIds, setOptimisticFormIds] = useState<string[]>(target.attachedFormIds ?? EMPTY_IDS)
  const [optimisticDesignIds, setOptimisticDesignIds] = useState<string[]>(target.attachedDesignIds ?? EMPTY_IDS)
  const [optimisticUploadIds, setOptimisticUploadIds] = useState<string[]>(target.attachedUploadIds ?? EMPTY_IDS)
  const [optimisticLinkIds, setOptimisticLinkIds] = useState<string[]>(target.attachedLinkIds ?? EMPTY_IDS)
  const [isAttachPickerOpen, setIsAttachPickerOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const wsId = target.workspaceId || activeWorkspaceId
    if (!wsId || !currentUser?.uid) return
    return subscribeWorkspaceFiles(wsId, currentUser.uid, false, setWorkspaceFiles)
  }, [activeWorkspaceId, currentUser?.uid, target.workspaceId])

  const [prevFormIds, setPrevFormIds] = useState(target.attachedFormIds)
  if (target.attachedFormIds !== prevFormIds) {
    setPrevFormIds(target.attachedFormIds)
    setOptimisticFormIds(target.attachedFormIds ?? EMPTY_IDS)
  }

  const [prevDesignIds, setPrevDesignIds] = useState(target.attachedDesignIds)
  if (target.attachedDesignIds !== prevDesignIds) {
    setPrevDesignIds(target.attachedDesignIds)
    setOptimisticDesignIds(target.attachedDesignIds ?? EMPTY_IDS)
  }

  const [prevUploadIds, setPrevUploadIds] = useState(target.attachedUploadIds)
  if (target.attachedUploadIds !== prevUploadIds) {
    setPrevUploadIds(target.attachedUploadIds)
    setOptimisticUploadIds(target.attachedUploadIds ?? EMPTY_IDS)
  }

  const [prevLinkIds, setPrevLinkIds] = useState(target.attachedLinkIds)
  if (target.attachedLinkIds !== prevLinkIds) {
    setPrevLinkIds(target.attachedLinkIds)
    setOptimisticLinkIds(target.attachedLinkIds ?? EMPTY_IDS)
  }

  const activeWorkspace = workspaces.find(
    (workspace) => workspace.id === target.workspaceId || workspace.id === activeWorkspaceId,
  )
  const currency = activeWorkspace?.settings?.currency ?? 'USD'

  const formTitleById = useMemo(
    () => new Map(formsPage.items.map((form) => [form.id, form.title])),
    [formsPage.items],
  )
  const designTitleById = useMemo(
    () => new Map(designsPage.items.map((design) => [design.id, design.title])),
    [designsPage.items],
  )
  const uploadTitleById = useMemo(
    () => new Map(workspaceFiles.map((file) => [file.id, file.fileName || 'Untitled file'])),
    [workspaceFiles],
  )
  const linkTitleById = useMemo(
    () => new Map(workspaceLinks.map((link) => [link.id, link.title || 'Untitled link'])),
    [workspaceLinks],
  )

  const detachForm = async (formId: string) => {
    if (!canEdit || busy) return
    setBusy(true)
    try {
      await detachFormFromItem({ ...target, attachedFormIds: optimisticFormIds }, formId)
      setOptimisticFormIds((current) => current.filter((id) => id !== formId))
      showToast('Form detached from item.', 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not detach form.', 'error')
    } finally {
      setBusy(false)
    }
  }

  const detachDesign = async (designId: string) => {
    if (!canEdit || busy) return
    setBusy(true)
    try {
      await detachDesignFromItem({ ...target, attachedDesignIds: optimisticDesignIds }, designId)
      setOptimisticDesignIds((current) => current.filter((id) => id !== designId))
      showToast('Design detached from item.', 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not detach design.', 'error')
    } finally {
      setBusy(false)
    }
  }

  const detachUpload = async (uploadId: string) => {
    if (!canEdit || busy) return
    setBusy(true)
    try {
      await detachUploadFromItem({ ...target, attachedUploadIds: optimisticUploadIds }, uploadId)
      setOptimisticUploadIds((current) => current.filter((id) => id !== uploadId))
      showToast('File detached from item.', 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not detach file.', 'error')
    } finally {
      setBusy(false)
    }
  }

  const detachLink = async (linkId: string) => {
    if (!canEdit || busy) return
    setBusy(true)
    try {
      await detachLinkFromItem({ ...target, attachedLinkIds: optimisticLinkIds }, linkId)
      setOptimisticLinkIds((current) => current.filter((id) => id !== linkId))
      showToast('Link detached from item.', 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not detach link.', 'error')
    } finally {
      setBusy(false)
    }
  }

  const formChips = optimisticFormIds.map((id) => ({
    id,
    title: formTitleById.get(id) ?? 'Attached form',
  }))
  const designChips = optimisticDesignIds.map((id) => ({
    id,
    title: designTitleById.get(id) ?? 'Attached design',
  }))
  const uploadChips = optimisticUploadIds.map((id) => ({
    id,
    title: uploadTitleById.get(id) ?? 'Attached file',
  }))
  const linkChips = optimisticLinkIds.map((id) => ({
    id,
    title: linkTitleById.get(id) ?? 'Attached link',
  }))

  const hasNoAttachments =
    formChips.length === 0 && designChips.length === 0 && uploadChips.length === 0 && linkChips.length === 0

  const budgetLabel = formatMinorCurrency(budgetLimit ?? estimatedCost ?? null, currency)

  const attachWorkspaceItems = async (refs: WorkspaceItemReference[]) => {
    if (!canEdit || busy) return
    const attachedIds = new Set([
      ...optimisticFormIds,
      ...optimisticDesignIds,
      ...optimisticUploadIds,
      ...optimisticLinkIds,
    ])
    const uniqueRefs = refs.filter((ref) => !attachedIds.has(ref.id) && (attachedIds.add(ref.id), true))
    if (!uniqueRefs.length) return

    setBusy(true)
    try {
      const results = await Promise.allSettled(uniqueRefs.map(async (ref) => {
        if (ref.kind === 'form') await attachFormToItem(target, ref.id)
        else if (ref.kind === 'design') await attachDesignToItem(target, ref.id)
        else if (ref.kind === 'upload') await attachUploadToItem(target, ref.id)
        else if (ref.kind === 'link') await attachLinkToItem(target, ref.id)
        else throw new Error(`Unsupported attachment kind: ${ref.kind}`)
        return ref
      }))
      const attached = results.flatMap((result) => result.status === 'fulfilled' ? [result.value] : [])
      if (attached.length) {
        setOptimisticFormIds((current) => Array.from(new Set([...current, ...attached.filter((ref) => ref.kind === 'form').map((ref) => ref.id)])))
        setOptimisticDesignIds((current) => Array.from(new Set([...current, ...attached.filter((ref) => ref.kind === 'design').map((ref) => ref.id)])))
        setOptimisticUploadIds((current) => Array.from(new Set([...current, ...attached.filter((ref) => ref.kind === 'upload').map((ref) => ref.id)])))
        setOptimisticLinkIds((current) => Array.from(new Set([...current, ...attached.filter((ref) => ref.kind === 'link').map((ref) => ref.id)])))
      }
      const failed = results.length - attached.length
      if (attached.length && !failed) {
        showToast(`${attached.length} item${attached.length === 1 ? '' : 's'} attached.`, 'success')
      } else if (attached.length) {
        showToast(`${attached.length} attached · ${failed} failed`, 'error')
      } else {
        showToast('Could not attach items.', 'error')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <section className="itemAttachmentsPanel itemAttachmentsPanel--checklistPopup" aria-label="Item attachments">
        <header className="itemProvisionsHeader">
          <div className="itemProvisionsTitleGroup">
            <span className="formsEyebrow">Item provisions</span>
            {formsPage.loading || designsPage.loading ? (
              <MiniLoader size="xs" label="Loading attachments" inline />
            ) : null}
          </div>
          {canEdit && (
            <Button
              variant="tertiary"
              size="sm"
              className="itemProvisionsAttachBtn"
              onClick={() => setIsAttachPickerOpen(true)}
              aria-label="Attach provision"
              icon={<AddRoundedIcon fontSize="inherit" />}
              data-tour-id={attachTourId}
            >
              Attach
            </Button>
          )}
        </header>

        <div className="itemProvisionsRows">
          {hasNoAttachments && (
            <div className="emptyHubState emptyHubState--compact" style={{ padding: '16px 12px', margin: '4px 0', textAlign: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, color: 'var(--muted)', marginBottom: 4 }}>
                <AttachFileRoundedIcon fontSize="small" />
                <strong style={{ fontSize: 13, color: 'var(--text-strong)' }}>Nothing attached yet.</strong>
              </div>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--muted)', lineHeight: 1.4 }}>
                Attach forms, designs, files, or links to give this item everything it needs.
              </p>
            </div>
          )}

          {formChips.length > 0 && (
            <article className="itemProvisionsRow">
              <AssignmentRoundedIcon aria-hidden="true" />
              <div className="itemProvisionsContent">
                <strong>Forms</strong>
                <small>{`Forms · ${formChips.length} attached`}</small>
                <div className="itemProvisionChips">
                  {formChips.map((form) => (
                    <TagChip
                      key={form.id}
                      removeLabel={`Detach ${form.title}`}
                      disabled={busy}
                      onRemove={canEdit ? () => void detachForm(form.id) : undefined}
                    >
                      {form.title}
                    </TagChip>
                  ))}
                </div>
              </div>
            </article>
          )}

          {designChips.length > 0 && (
            <article className="itemProvisionsRow">
              <BrushRoundedIcon aria-hidden="true" />
              <div className="itemProvisionsContent">
                <strong>Designs</strong>
                <small>{`Designs · ${designChips.length} attached`}</small>
                <div className="itemProvisionChips">
                  {designChips.map((design) => (
                    <TagChip
                      key={design.id}
                      removeLabel={`Detach ${design.title}`}
                      disabled={busy}
                      onRemove={canEdit ? () => void detachDesign(design.id) : undefined}
                    >
                      {design.title}
                    </TagChip>
                  ))}
                </div>
              </div>
            </article>
          )}

          {uploadChips.length > 0 && (
            <article className="itemProvisionsRow">
              <FolderOpenRoundedIcon aria-hidden="true" />
              <div className="itemProvisionsContent">
                <strong>Files</strong>
                <small>{`Files · ${uploadChips.length} attached`}</small>
                <div className="itemProvisionChips">
                  {uploadChips.map((file) => (
                    <TagChip
                      key={file.id}
                      removeLabel={`Detach ${file.title}`}
                      disabled={busy}
                      onRemove={canEdit ? () => void detachUpload(file.id) : undefined}
                    >
                      {file.title}
                    </TagChip>
                  ))}
                </div>
              </div>
            </article>
          )}

          {linkChips.length > 0 && (
            <article className="itemProvisionsRow">
              <LinkRoundedIcon aria-hidden="true" />
              <div className="itemProvisionsContent">
                <strong>Links</strong>
                <small>{`Links · ${linkChips.length} attached`}</small>
                <div className="itemProvisionChips">
                  {linkChips.map((link) => (
                    <TagChip
                      key={link.id}
                      removeLabel={`Detach ${link.title}`}
                      disabled={busy}
                      onRemove={canEdit ? () => void detachLink(link.id) : undefined}
                    >
                      {link.title}
                    </TagChip>
                  ))}
                </div>
              </div>
            </article>
          )}

          <article className="itemProvisionsRow">
            <AccountBalanceWalletRoundedIcon aria-hidden="true" />
            <div className="itemProvisionsContent">
              <strong>Budget</strong>
              <small>{`Budget · ${budgetLabel}`}</small>
            </div>
            {canEdit && onSetBudgetLimit && (
              <div className="itemProvisionAction">
                <Button variant="tertiary" size="sm" onClick={onSetBudgetLimit} icon={<AccountBalanceWalletRoundedIcon fontSize="inherit" />}>
                  Set limit
                </Button>
              </div>
            )}
          </article>
        </div>
      </section>

      {isAttachPickerOpen && (
        <AppDialog
          open={isAttachPickerOpen}
          onDismiss={() => setIsAttachPickerOpen(false)}
          title="Attach provisions"
          size="attachPicker"
          actions={[]}
          message={
            <WorkspaceItemPicker
              target={target}
              workspaceId={target.workspaceId || activeWorkspaceId}
              userId={currentUser?.uid}
              canEdit={canEdit}
              forms={formsPage.items}
              designs={designsPage.items}
              uploads={workspaceFiles.map((file) => ({
                id: file.id,
                name: file.fileName,
                title: file.fileName,
                sizeBytes: file.sizeBytes,
                mimeType: file.mimeType,
              }))}
              links={workspaceLinks}
              enabledKinds={['forms', 'designs', 'uploads', 'links']}
              multiSelect
              selectedIds={
                new Set([
                  ...optimisticFormIds,
                  ...optimisticDesignIds,
                  ...optimisticUploadIds,
                  ...optimisticLinkIds,
                ])
              }
              onCommitRefs={(refs) => { void attachWorkspaceItems(refs) }}
              onDetach={async (kind, id) => {
                if (kind === 'forms') {
                  await detachForm(id)
                } else if (kind === 'designs') {
                  await detachDesign(id)
                } else if (kind === 'uploads') {
                  await detachUpload(id)
                } else if (kind === 'links') {
                  await detachLink(id)
                }
              }}
              onClose={() => setIsAttachPickerOpen(false)}
            />
          }
        />
      )}
    </>
  )
}
